/**
 * Outlet Exceptions Config
 *
 * Covers outlet exception codes:
 * - ex-outlet-ls-address-incorrect
 * - ex-outlet-ls-dnu
 *
 * Displays outlet address fields with highlight for missing data,
 * DNU chip, sales count, and edit action button.
 */
import React from 'react'
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { withTooltip } from '../shared/muiElements'
import {
  Store as StoreIcon,
  Edit as EditIcon,
  PointOfSale as RegisterIcon,
} from '@mui/icons-material'
import { handleExternalLinkClick } from '../../../utils/externalLinks'
import { buildIssuesColumn } from '../shared/issuesColumn'
import { PLATFORM_CONTEXT_COLUMNS } from '../shared/platformColumns'
import { composeRowSubtitle, textPart } from '../shared/rowSubtitle'
import type { ExceptionConfig, ExceptionColumnDef } from '../types'
import { INVALID_CELL_BG } from '../../../theme/exceptionTableStyles'
import { statusPillChipProps } from '../../../components/shared/AppDrawer/chipPresets'

const isFieldInvalid = (value?: string | null): boolean => !value || value.length < 3

const renderOutlet = (value: any, row: Record<string, any>): React.ReactNode =>
  React.createElement(
    Box,
    { sx: { display: 'flex', alignItems: 'center', gap: 1 } },
    React.createElement(StoreIcon, { color: 'action', fontSize: 'small' }),
    React.createElement(Typography, { variant: 'body2', fontWeight: 'medium' }, value),
    row.is_dnu &&
      React.createElement(Chip, statusPillChipProps('DNU', 'error'))
  )

const addressCellStyle = (value: any): Record<string, any> => {
  if (isFieldInvalid(value)) {
    return { backgroundColor: INVALID_CELL_BG }
  }
  return {}
}

const renderAddressField = (value: any): React.ReactNode =>
  React.createElement(
    Typography,
    {
      variant: 'body2',
      color: isFieldInvalid(value) ? 'error' : 'inherit',
    },
    value || '-'
  )

const renderSalesCount = (value: any): React.ReactNode =>
  React.createElement(Chip, {
    label: value !== undefined ? value.toLocaleString() : '\u2014',
    size: 'small',
    variant: 'outlined',
    color: value === 0 ? 'warning' : 'default',
  })

const formatLastSaleDate = (value: any): React.ReactNode => {
  if (!value) return '\u2014'
  const date = new Date(value)
  return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
}

const renderRegisters = (value: any): React.ReactNode =>
  React.createElement(
    Box,
    { sx: { display: 'flex', alignItems: 'center', gap: 0.5 } },
    React.createElement(RegisterIcon, { fontSize: 'small' }),
    React.createElement(Typography, { variant: 'body2' }, value || 0)
  )

const renderActions = (value: any, row: Record<string, any>): React.ReactNode => {
  if (!row.lightspeed_outlet_edit_link) return null
  return withTooltip(
    { title: 'Edit Outlet' },
    React.createElement(
      IconButton,
      {
        size: 'small',
        onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault()
          handleExternalLinkClick(
            e as any,
            row.lightspeed_outlet_edit_link,
            'Edit Outlet'
          )
        },
        sx: { color: 'action.active' },
      },
      React.createElement(EditIcon, { fontSize: 'medium' })
    )
  )
}

const inspectorColumns: ExceptionColumnDef[] = [
  {
    key: 'outlet_name',
    label: 'Outlet',
    contexts: ['inspector'],
    render: renderOutlet,
  },
  {
    key: 'physical_address_1',
    label: 'Address',
    contexts: ['inspector'],
    render: renderAddressField,
    getCellStyle: addressCellStyle,
  },
  {
    key: 'physical_suburb',
    label: 'Suburb',
    contexts: ['inspector'],
    render: renderAddressField,
    getCellStyle: addressCellStyle,
  },
  {
    key: 'physical_city',
    label: 'City',
    contexts: ['inspector'],
    render: renderAddressField,
    getCellStyle: addressCellStyle,
  },
  {
    key: 'physical_state',
    label: 'State',
    contexts: ['inspector'],
    render: renderAddressField,
    getCellStyle: addressCellStyle,
  },
  {
    key: 'physical_postcode',
    label: 'Postcode',
    contexts: ['inspector'],
    render: renderAddressField,
    getCellStyle: addressCellStyle,
  },
  {
    key: 'sales_count',
    label: '# of Sales',
    align: 'center',
    contexts: ['inspector'],
    render: renderSalesCount,
    getSortValue: (row) => row.sales_count ?? 0,
  },
  {
    key: 'last_sale_date',
    label: 'Last Sale',
    align: 'center',
    contexts: ['inspector'],
    render: formatLastSaleDate,
    getSortValue: (row) => row.last_sale_date || '',
  },
  {
    key: 'register_count',
    label: 'Registers',
    contexts: ['inspector'],
    render: renderRegisters,
    getSortValue: (row) => row.register_count ?? 0,
  },
  {
    key: 'actions',
    label: 'Actions',
    contexts: ['inspector'],
    render: renderActions,
  },
]

const platformColumns: ExceptionColumnDef[] = [
  ...PLATFORM_CONTEXT_COLUMNS,
  {
    key: 'outlet_name',
    label: 'Outlet',
    contexts: ['platform'],
  },
  {
    key: 'physical_address_1',
    label: 'Address',
    contexts: ['platform'],
  },
  {
    key: 'physical_suburb',
    label: 'Suburb',
    contexts: ['platform'],
  },
  {
    key: 'physical_city',
    label: 'City',
    contexts: ['platform'],
  },
  {
    key: 'physical_state',
    label: 'State',
    contexts: ['platform'],
  },
  {
    key: 'physical_postcode',
    label: 'Postcode',
    contexts: ['platform'],
  },
]

export const outletConfig: ExceptionConfig = {
  codes: [
    'ex-outlet-ls-address-incorrect',
    'ex-outlet-ls-dnu',
  ],
  columns: [buildIssuesColumn(), ...inspectorColumns, ...platformColumns],
  defaultSortColumn: 'outlet_name',
  defaultSortDirection: 'asc',
  complexity: 'simple',
  rowKeyField: 'ls_id',
  getRowSubtitle: (row) =>
    composeRowSubtitle([
      [textPart(row.physical_suburb), textPart(row.physical_state)]
        .filter(Boolean)
        .join(', ') || null,
      typeof row.register_count === 'number' && row.register_count > 0
        ? `${row.register_count} register${row.register_count === 1 ? '' : 's'}`
        : null,
      typeof row.sales_count === 'number' && row.sales_count > 0
        ? `${row.sales_count.toLocaleString()} sales`
        : null,
    ]),
}
